// ────────────────────────────────────────────────────────────────────
// App root — Router + QueryClient providers.
//
// Route map (workbench roadmap A2; revised 2026-09-01, spec:
// docs/superpowers/specs/2026-09-01-today-is-the-queue-design.md):
//   /login         → LoginScreen (public)
//   /guest/:token  → GuestLandingScreen (public — scoped, expiring,
//                    read-only third-party view; roadmap D2, 2026-07-23)
//   /              → /today. TODAY is the landing.
//   /queue         → QueueScreen — the STAFF pipeline: one list, split by
//                    whose move it is, filtered whole by stage or by client.
//                    GET /api/queue is require_role(*STAFF_ROLES), so this is
//                    a staff destination and the nav slot is gated to match.
//   /today         → TodayScreen — the ball-in-court list, รอตรวจ, the fleet's
//                    due dates, and the only free-form way to open a job. NOT
//                    superseded by /queue: a customer has no pipeline at all,
//                    and the three sections above are not on the queue.
//   /jobs/:id      → JobScreen — one งาน's workspace
//   /atlas         → AtlasScreen (Site Atlas — an ordinary tool now, reached
//                    from the menu bar, not the landing page)
//
// ── Three routes retired on 2026-09-01 ──────────────────────────────────
// The nav's `work` group offered five destinations to one question — "what
// should I be doing?" Three of them are these: "/" (DeskHomeScreen),
// "/submissions" (SubmissionsScreen) and "/reviews" (PendingReviewsScreen,
// already deleted in the change before this one). TODAY holds what all three
// held; DeskHomeScreen and SubmissionsScreen are deleted with this change.
//
// They retire AS REDIRECTS rather than being deleted outright the way /doeb
// and /ndt were, and the difference is not squeamishness: those two were
// placeholders nothing linked to, while these three are in bookmarks, in
// pasted links and in muscle memory. A route that retires must hand its
// destinations to one that stays, and "/reviews" keeps its ?queue=review so
// it lands on the same ROWS, not merely on the same screen.
//
// Future routes (Phase 2-5):
//   /sites/:id          → Sub-Atlas (areas within plant)
//   /areas/:id          → Plot plan + assets
//   /assets/:id         → Asset workspace (3D + history)
//   /inspections/:id    → Inspection detail (iframe embed)
//
// Note: Asset Studio (internal staff tool for managing the hierarchy)
// lives at /asset_studio.html alongside the other admin pages, NOT in
// this portal app — consistent with the other studios (C-Scan, Image).
// ────────────────────────────────────────────────────────────────────

import { Suspense, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Navigate, RouterProvider, createBrowserRouter } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import LoginScreen from "@/screens/LoginScreen";
import GuestLandingScreen from "@/screens/GuestLandingScreen";
import TodayScreen from "@/screens/TodayScreen";
import AtlasScreen from "@/screens/AtlasScreen";
import SiteMapScreen from "@/screens/SiteMapScreen";
import AssetWorkspaceScreen from "@/screens/AssetWorkspaceScreen";
import DeskScreen from "@/screens/DeskScreen";
import InspectionDetailScreen from "@/screens/InspectionDetailScreen";
import InboxScreen from "@/screens/InboxScreen";
import ReportsScreen from "@/screens/ReportsScreen";
import IntakeScreen from "@/screens/IntakeScreen";
import DocumentsScreen from "@/screens/DocumentsScreen";
import JobScreen from "@/screens/JobScreen";
import QueueScreen from "@/screens/queue/QueueScreen";
import SettingsScreen from "@/screens/SettingsScreen";
import { RequireAuth } from "@/lib/auth";

// Report Review Room (roadmap 3.1) — pdf.js (~1MB) must stay OUT of the main
// bundle, so this route (and only this route) is code-split. The Suspense
// fallback only ever flashes on a direct/refresh load of /review/:fileId;
// every in-app navigation there has the chunk prefetched by the browser's
// own module cache once visited once.
const ReviewScreen = lazy(() => import("@/screens/ReviewScreen"));
function ReviewScreenLazy() {
  return (
    <Suspense fallback={<div className="p-6 font-mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>กำลังโหลด…</div>}>
      <ReviewScreen />
    </Suspense>
  );
}

// Exported (not just used inline below) so App.test.tsx can assert the route
// wiring — that "/" redirects to TODAY, that the retired paths still land on
// the list they named — without mounting the whole authenticated shell
// (AppShell alone pulls in Leaflet, the dock system, and a dozen apiClient
// calls; a route-config assertion is the cheap, non-brittle way to pin this
// contract down).
export const APP_ROUTE_CHILDREN = [
  // TODAY is the landing. DeskHomeScreen — a greeting and one card whose
  // destination was the file room — is deleted; FILES already IS what that
  // card opened, so nothing was rebuilt, and the one behaviour the card
  // carried alone (refusing to invite an import while no tenant is bound)
  // moved to the surface that now carries the invitation. See IntakeScreen.
  { index: true, element: <Navigate to="/today" replace /> },
  { path: "today", element: <TodayScreen /> },
  // QUEUE (2026-09-01, plan Task 4) — the design's first slot, mounted. The
  // screen fires its own verb: POST /api/submissions/{id}/advance, whose
  // refusal comes from the route's stage gate and is printed in the server's
  // own words. Nothing in the browser re-declares who may move a job.
  { path: "queue", element: <QueueScreen /> },
  { path: "atlas", element: <AtlasScreen /> },
  // /doeb and /ndt are gone (2026-08-30). Both were bare PlaceholderScreens
  // reading "will live here", with no commits in three months and nothing in
  // the nav, the tree or the desk linking to them — a route that promises a
  // capability and delivers a Coming soon badge is worse than a 404, because
  // it is the app itself making the claim. PlaceholderScreen went with them:
  // they were its only two consumers.
  // Files: ONE destination, three lenses on the same pile (FileTabs' docstring
  // has the why). The view lives in the path so a tab is bookmarkable and the
  // back button behaves. /intake and /documents stay as redirects — they are in
  // the desk card, the left tree, and anything anyone has already bookmarked.
  { path: "files/queue",    element: <IntakeScreen lens="queue" /> },
  { path: "files/assets",   element: <IntakeScreen lens="assets" /> },
  { path: "files/packages", element: <DocumentsScreen /> },
  { path: "files",     element: <Navigate to="/files/queue" replace /> },
  { path: "intake",    element: <Navigate to="/files/queue" replace /> },
  { path: "documents", element: <Navigate to="/files/packages" replace /> },
  // The work queue is TODAY. SubmissionsScreen was master-detail — a queue
  // beside a workspace — and both halves have moved: the queue (with its
  // status filter) to /today, the workspace to /jobs/:id. Every งาน it could
  // reach is on TODAY's ทั้งหมด lens, asserted by count in TodayScreen.test.
  { path: "submissions", element: <Navigate to="/today" replace /> },
  // JOB — one งาน's workspace, lifted out of SubmissionsScreen's right half
  // (spec: 2026-09-01-today-is-the-queue-design.md). Staff only; the screen
  // sends a customer to /files/queue itself.
  { path: "jobs/:id", element: <JobScreen /> },
  // รอตรวจ is a FILTER on TODAY now, not a route of its own
  // (2026-09-01, spec: TODAY is the queue). PendingReviewsScreen is deleted;
  // the path keeps ?queue=review so a bookmark lands on the same rows.
  { path: "reviews", element: <Navigate to="/today?queue=review" replace /> },
  { path: "review/:fileId", element: <ReviewScreenLazy /> },
  { path: "sites/:plantId", element: <SiteMapScreen /> },
  // /command merged into the plant page as its default "Headline" view.
  { path: "command/:plantId", element: <Navigate to="/atlas" replace /> },
  // /areas removed — the plant page IS the asset worklist (area is a filter,
  // not a separate, inferior level). Stray /areas/* links fall through to the
  // catch-all → "/" (the desk). It said → /atlas until 2026-08-30; the router
  // at the bottom of this file has always sent "*" to "/".
  { path: "assets/:assetId", element: <AssetWorkspaceScreen /> },
  // Desk (workbench roadmap C1) — a path param, not a query flag on the
  // asset workspace route: it's a distinct staff-only surface (own chrome,
  // own audience), same pattern as /review/:fileId being separate from
  // /documents. See DeskScreen.tsx's header comment for the full rationale.
  { path: "desk/:assetId", element: <DeskScreen /> },
  { path: "inspections/:inspectionId", element: <InspectionDetailScreen /> },
  { path: "inbox",    element: <InboxScreen /> },
  { path: "reports",  element: <ReportsScreen /> },
  { path: "settings", element: <SettingsScreen /> },
];

const router = createBrowserRouter([
  { path: "/login", element: <LoginScreen /> },
  // Guest access (workbench roadmap D2) — a scoped, expiring, read-only
  // view for a third party. PUBLIC route, outside RequireAuth/AppShell like
  // /login above: a guest has no `users` row and must see NO menu bar, left
  // tree, or dock — GuestLandingScreen is entirely self-contained chrome.
  { path: "/guest/:token", element: <GuestLandingScreen /> },
  {
    path: "/",
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: APP_ROUTE_CHILDREN,
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);
// The v6 `future` opt-ins that used to sit here (v7_relativeSplatPath, plus
// v7_startTransition on RouterProvider below) ARE the default in v7 — this app
// was already running v7 routing behaviour, so the upgrade is their removal.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
